<!-- EditNoteObservationForm.vue -->
<script setup>
import { ref, onMounted, watch } from "vue";
import { useNoteStore } from "@/stores/useNoteStore";
import { useActivityStore } from "@/stores/useActivityStore";
import { useMemberNoteReview } from "@/composables/useMemberNoteReview";
import * as activityService from "@/services/activityService";

import CustomTextarea from "../Generics/CustomTextarea.vue";
import { useDraggableModal } from "@/composables/useDraggableModal";

const props = defineProps({
  showObservationForm: Boolean,
  noteId: {
    type: Number,
    required: true,
  },
});

const emit = defineEmits(["close"]);

const noteStore = useNoteStore();
const activityStore = useActivityStore();
const { isManagedMember, canReviewNoteAsCorrection } = useMemberNoteReview();

const isSubmitting = ref(false);
const observationInput = ref(null);

// 🔹 NOVO: arrastar o modal pelo cabeçalho. Nem todo uso desmonta o componente
// entre aberturas, então o watch recentraliza ao reabrir.
const {
  panelRef,
  panelStyle,
  isDragging,
  isDraggable,
  onHandlePointerDown,
  resetPosition,
} = useDraggableModal();

watch(
  () => props.showObservationForm,
  (val) => {
    if (val) resetPosition();
  }
);

// Só true quando o usuário logado é o titular, criador da atividade da nota,
// e o autor da nota estava atuando como membro gerenciado dele ao criá-la.
const allowCorrectionOptions = ref(false);

const observationTypeOptions = [
  { value: "Correct", label: "Certo", colorClass: "green", icon: "fas fa-check" },
  { value: "Incorrect", label: "Errado", colorClass: "red", icon: "fas fa-times" },
];

const obj = ref({
  noteId: props.noteId,
  observation: "",
  observationType: "Observation",
});

// Mutuamente exclusivos: clicar no já marcado desmarca, voltando ao estado
// "nenhum marcado" (observationType = "Observation", sem botão próprio —
// significa observação sem sinalização de certo/errado).
function toggleObservationType(value) {
  obj.value.observationType =
    obj.value.observationType === value ? "Observation" : value;
}

function closeModal() {
  emit("close");
}

async function editarObservacao() {
  if (isSubmitting.value) return;
  isSubmitting.value = true;

  try {
    await noteStore.updateNoteObservation(
      props.noteId,
      obj.value.observation,
      obj.value.observationType,
    );
    await activityStore.getActivityById(activityStore.idActivity);
    closeModal();
  } catch (err) {
    console.error("🛑 Erro ao atualizar a observação da nota:", err);
    alert("Não foi possível salvar a observação. " + (err.message || ""));
  } finally {
    isSubmitting.value = false;
  }
}

onMounted(async () => {
  try {
    const currentNoteData = await noteStore.getNoteById(props.noteId);
    if (!currentNoteData) {
      alert("Erro: A nota para edição não foi encontrada.");
      closeModal();
      return;
    }

    obj.value.observation = currentNoteData.observation || "";
    obj.value.observationType = currentNoteData.observationType || "Observation";

    // Só vale a pena checar se o usuário logado não estiver ele mesmo em
    // contexto managed (aí nunca pode classificar).
    if (!isManagedMember.value) {
      try {
        const activityResponse = await activityService.getActivityById(
          currentNoteData.activityId,
        );

        allowCorrectionOptions.value = canReviewNoteAsCorrection(
          currentNoteData.createdAsMemberOfUserId,
          activityResponse.data?.createdByUserId,
        );
      } catch (permError) {
        console.warn(
          "⚠️ Não foi possível verificar a atividade para exibir Certo/Errado:",
          permError,
        );
        allowCorrectionOptions.value = false;
      }
    }

    observationInput.value?.focus?.();
  } catch (error) {
    console.error("🛑 Erro ao carregar a nota:", error);
    alert("Não foi possível carregar os dados da nota.");
    closeModal();
  }
});

</script>

<template>
  <!-- Sem fechamento por clique no backdrop: o formulário guarda texto digitado,
       e um clique fora perderia a observação em andamento. A saída é explícita —
       botão "Cancelar" ou o X do cabeçalho. -->
  <div v-if="showObservationForm" class="modal-backdrop">
    <div ref="panelRef" class="modal-panel small" :style="panelStyle">
      <div
        class="modal-header"
        :class="{
 'modal-header--draggable': isDraggable,
          'modal-header--dragging': isDragging,
        }"
        @pointerdown="onHandlePointerDown"
      >
        <h3 class="modal-title">Inserir Observação na Nota</h3>

        <button
          @click="closeModal"
          type="button"
          class="modal-close-btn"
          :disabled="isSubmitting"
        >
          <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
            <path
              fill-rule="evenodd"
              d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
              clip-rule="evenodd"
            />
          </svg>
        </button>
      </div>

      <form @submit.prevent="editarObservacao" novalidate>
        <div class="modal-body">
          <div class="form-group">
            <label class="form-label">Descrição</label>

            <CustomTextarea
              v-model:body="obj.observation"
              ref="observationInput"
              placeholder="Digite a descrição (opcional)"
              style="min-height: 120px"
            />
          </div>

          <div v-if="allowCorrectionOptions" class="form-group form-group--type">
            <label class="form-label">Quero sinalizar como:</label>

            <div class="tab-buttons-wrapper">
              <div class="tab-buttons">
                <button
                  v-for="option in observationTypeOptions"
                  :key="option.value"
                  type="button"
                  class="tab-button"
                  :class="[
 option.colorClass,
                    { active: obj.observationType === option.value },
                  ]"
                  :disabled="isSubmitting"
                  @click="toggleObservationType(option.value)"
                >
                  <i :class="option.icon"></i>
                  {{ option.label }}
                </button>
              </div>
            </div>
          </div>
        </div>

        <div class="modal-footer">
          <button
            type="button"
            @click="closeModal"
            class="btn btn-secondary"
            :disabled="isSubmitting"
          >
            Cancelar
          </button>

          <button
            type="submit"
            class="btn btn-primary-purple"
            :disabled="isSubmitting"
          >
            <svg
              v-if="isSubmitting"
              class="w-5 h-5 mr-2 animate-spin"
              viewBox="0 0 24 24"
              fill="none"
            >
              <circle
                class="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                stroke-width="4"
              />
              <path
                class="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
              />
            </svg>

            <svg
              v-else
              class="mr-1 -ml-1 w-5 h-5"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                d="M16.704 4.153a.75.75 0 0 1 .143 1.052l-8 10.5a.75.75 0 0 1-1.127.075l-4.5-4.5a.75.75 0 0 1 1.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 0 1 1.05-.143Z"
              />
            </svg>

            <span>{{
              isSubmitting ? "Salvando..." : "Salvar Observação"
            }}</span>
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<style scoped>
.form-group--type {
  margin-top: 1.25rem;
}

/* =========================
   SELETOR — Observação (azul) / Certo (verde) / Errado (vermelho)
   Mesmo estilo de .tab-buttons do SupportEmailModal.vue
========================= */
.tab-buttons-wrapper {
  display: flex;
  justify-content: flex-start;
}

.tab-buttons {
  display: inline-flex;
  gap: 0.25rem;
}

.tab-button {
  border: 1px solid var(--border-color);
  background: transparent;
  padding: 0.35rem 0.9rem;
  margin-right: 0.25rem;
  border-radius: 12px;
  font-size: 0.85rem;
  font-weight: 500;
  line-height: 1;
  color: var(--text-muted);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.25rem;
  min-width: 7.75rem;
  cursor: pointer;
  transition: background-color 0.2s ease-in-out, transform 0.05s ease;
}

.tab-button i {
  line-height: 1;
}

.tab-button:hover:not(:disabled):not(.active) {
  background-color: var(--surface-hover);
}

.tab-button:focus,
.tab-button:focus-visible {
  outline: none;
  box-shadow: none;
}

.tab-button:active:not(:disabled) {
  transform: translateY(1px);
}

.tab-button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* Ativo = uma cor por opção */
.tab-button.blue.active {
  background-color: var(--info-border);
  color: var(--info-text);
  border-color: #93c5fd;
  transform: scale(1.07);
}

.tab-button.green.active {
  background-color: var(--success-bg);
  color: var(--success-text);
  border-color: #86efac;
  transform: scale(1.07);
}

.tab-button.red.active {
  background-color: var(--danger-bg);
  color: var(--danger-text);
  border-color: var(--danger);
  transform: scale(1.07);
}

/* Primário roxo (Salvar Observação) — mesmo roxo do .btn-success do EditNoteForm */
.btn-primary-purple {
  background-color: var(--brand);
  color: var(--text-on-brand);
  border-color: var(--brand);
  font-weight: 600;
}

.btn-primary-purple:hover:not(:disabled) {
  background-color: var(--brand-hover);
}

</style>
